import * as React from 'react';
import { isSupabase } from '@/lib/supabase';
import { getCourseProgressDetail } from '@/repos/progressRepo';
import { getAccessibleModuleIds } from '@/repos/moduleEnrollmentRepo';
import { 
  getUserEnrollmentStatus,
  getNextLessonToContinue,
  isCourseFavorited,
  addToFavorites,
  removeFromFavorites
} from '@/repos/userRepo';

export interface UserCourseProgress {
  courseId: string;
  totalLessons: number;
  completedLessons: number;
  progressPercentage: number;
  completedLessonIds: string[];
  moduleProgress: Record<string, number>;
  isEnrolled: boolean;
  isStarted: boolean;
  isCompleted: boolean;
  /** Matrícula parcial por módulo (sem curso completo) */
  hasPartialModuleAccess: boolean;
  accessibleModuleIds: string[];
  lastAccessedAt?: string;
  nextLesson?: {
    id: string;
    title: string;
    moduleId: string;
    moduleTitle: string;
  };
}

export interface UserProgressHook {
  progress: UserCourseProgress | null;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Progresso de lições usa `auth.users.id`; matrícula usa `users.id` (enrollments).
 * @param systemUserId — users.id (sessão systemUser), para enrollments
 * @param authUserId — Supabase Auth uid, para user_lesson_progress
 */
export function useUserProgress(
  systemUserId?: string,
  authUserId?: string,
  courseId?: string
): UserProgressHook {
  const [progress, setProgress] = React.useState<UserCourseProgress | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const fetchProgress = React.useCallback(async (forceRefresh = false) => {
    if (!authUserId || !courseId || !isSupabase()) {
      console.log('⚠️ useUserProgress: Parâmetros insuficientes ou não é Supabase');
      setProgress(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 useUserProgress: systemUserId:', systemUserId, 'authUserId:', authUserId, 'curso:', courseId, 'forceRefresh:', forceRefresh);

      const enrollmentStatus = systemUserId
        ? await getUserEnrollmentStatus(systemUserId, courseId)
        : {
            isEnrolled: false,
            isStarted: false,
            isCompleted: false,
          };

      let accessibleModuleIds: string[] = [];
      if (systemUserId && !enrollmentStatus.isEnrolled) {
        accessibleModuleIds = await getAccessibleModuleIds(systemUserId, courseId);
      }
      const hasPartialModuleAccess =
        !enrollmentStatus.isEnrolled && accessibleModuleIds.length > 0;
      const progressScope =
        enrollmentStatus.isEnrolled || accessibleModuleIds.length === 0
          ? null
          : accessibleModuleIds;

      const courseProgress = await getCourseProgressDetail(
        authUserId,
        courseId,
        progressScope
      );

      const hasLessonActivity =
        courseProgress.completedLessons > 0 || courseProgress.progressPercentage > 0;

      let nextLesson = undefined;
      const canContinue =
        enrollmentStatus.isEnrolled ||
        hasPartialModuleAccess ||
        hasLessonActivity;

      if (canContinue && !enrollmentStatus.isCompleted && courseProgress.progressPercentage < 100) {
        try {
          const nextLessonData = await getNextLessonToContinue(
            authUserId,
            courseId,
            progressScope
          );
          if (nextLessonData) {
            nextLesson = {
              id: nextLessonData.lesson_id,
              title: nextLessonData.lesson_title,
              moduleId: nextLessonData.module_id,
              moduleTitle: nextLessonData.module_title,
            };
          }
        } catch (nextLessonError) {
          console.warn('⚠️ Erro ao buscar próxima lição, continuando sem ela:', nextLessonError);
        }
      }

      const userProgress: UserCourseProgress = {
        courseId,
        totalLessons: courseProgress.totalLessons,
        completedLessons: courseProgress.completedLessons,
        progressPercentage: courseProgress.progressPercentage,
        completedLessonIds: courseProgress.completedLessonIds,
        moduleProgress: courseProgress.moduleProgress,
        isEnrolled: enrollmentStatus.isEnrolled,
        hasPartialModuleAccess,
        accessibleModuleIds,
        isStarted:
          enrollmentStatus.isStarted ||
          hasLessonActivity ||
          hasPartialModuleAccess,
        isCompleted: enrollmentStatus.isCompleted || courseProgress.progressPercentage >= 100,
        lastAccessedAt: enrollmentStatus.lastAccessedAt,
        nextLesson,
      };

      console.log('✅ useUserProgress: Progresso carregado:', userProgress);
      console.log('🔍 useUserProgress: Estado do botão será:', {
        isEnrolled: userProgress.isEnrolled,
        isStarted: userProgress.isStarted,
        buttonText: userProgress.isStarted ? 'Continuar Curso' : 'Iniciar Curso'
      });
      setProgress(userProgress);

    } catch (e: any) {
      console.error('❌ useUserProgress: Erro ao buscar progresso:', e);
      setError(e);
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }, [systemUserId, authUserId, courseId]);

  React.useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  return {
    progress,
    loading,
    error,
    refresh: () => fetchProgress(true) // Sempre forçar refresh quando chamado
  };
}

/**
 * Hook para gerenciar favoritos de cursos
 */
export function useCourseFavorites(userId?: string) {
  const [favorites, setFavorites] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const checkIfFavorited = React.useCallback(async (courseId: string): Promise<boolean> => {
    if (!userId || !isSupabase()) {
      return false;
    }

    try {
      return await isCourseFavorited(userId, courseId);
    } catch (e) {
      console.error('❌ Erro ao verificar favorito:', e);
      return false;
    }
  }, [userId]);

  const toggleFavorite = React.useCallback(async (courseId: string): Promise<boolean> => {
    if (!userId || !isSupabase()) {
      return false;
    }

    try {
      setLoading(true);
      setError(null);

      const isFavorited = await isCourseFavorited(userId, courseId);
      
      if (isFavorited) {
        await removeFromFavorites(userId, courseId);
        setFavorites(prev => prev.filter(id => id !== courseId));
        console.log('✅ Curso removido dos favoritos:', courseId);
        return false;
      } else {
        await addToFavorites(userId, courseId);
        setFavorites(prev => [...prev, courseId]);
        console.log('✅ Curso adicionado aos favoritos:', courseId);
        return true;
      }

    } catch (e: any) {
      console.error('❌ Erro ao alternar favorito:', e);
      setError(e);
      return false;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  return {
    favorites,
    loading,
    error,
    checkIfFavorited,
    toggleFavorite
  };
}
